/**
 * @fileoverview Check Email Page - Intermediate step after guest purchase
 * @module CheckEmailPage
 * 
 * Business Context:
 * - Displayed immediately after guest checkout completion
 * - Instructs users to check email for account setup link
 * - Prevents timing issues between checkout redirect and webhook execution
 * 
 * Technical Context:
 * - Handles both guest and existing user scenarios
 * - Provides fallback options if email doesn't arrive
 * - Links to support and login alternatives
 * 
 * Related Documents:
 * - PRD: Section 4.2 (Onboarding Phase)
 * - Architecture: Section 3.1 (Authentication Flow)
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { 
  Mail, 
  CheckCircle, 
  RefreshCw, 
  ArrowLeft,
  ExternalLink,
  Clock
} from 'lucide-react';

export default function CheckEmailPage() {
  const [searchParams] = useSearchParams();
  const [timeElapsed, setTimeElapsed] = useState(0);
  
  // Extract parameters from URL (passed from checkout success)
  const email = searchParams.get('email') || '';
  const sessionId = searchParams.get('session_id') || '';

  useEffect(() => {
    // Start timer to show elapsed time
    const interval = setInterval(() => {
      setTimeElapsed(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `${secs}s`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {/* Success Icon */}
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            Purchase Complete!
          </h1>
          
          {/* Subtitle */}
          <p className="text-gray-600 mb-6">
            Check your email to set up your account and access your personalized tax appeal course.
          </p>

          {/* Email Card */}
          <div className="bg-blue-50 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-center gap-3 mb-3">
              <Mail className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-blue-900">Email Sent To:</span>
            </div>
            <p className="text-blue-800 font-semibold break-all">
              {email || 'your registered email'}
            </p>
          </div>

          {/* Instructions */}
          <div className="text-left space-y-3 mb-6">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-blue-600 text-sm font-semibold">1</span>
              </div>
              <p className="text-gray-700 text-sm">
                Look for an email from <strong>Appeal Your Home</strong>
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-blue-600 text-sm font-semibold">2</span>
              </div>
              <p className="text-gray-700 text-sm">
                Click the <strong>"Complete Account Setup"</strong> button
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-blue-600 text-sm font-semibold">3</span>
              </div>
              <p className="text-gray-700 text-sm">
                Create your password and enter your property details
              </p>
            </div>
          </div>

          {/* Time Elapsed */}
          <div className="flex items-center justify-center gap-2 text-gray-500 text-sm mb-6">
            <Clock className="w-4 h-4" />
            <span>Waiting time: {formatTime(timeElapsed)}</span>
          </div>
        </div>

        {/* Help Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mt-4">
          <h3 className="font-semibold text-gray-900 mb-4 text-center">
            Need Help?
          </h3>
          
          <div className="space-y-3">
            {/* Check Spam */}
            <div className="text-center">
              <p className="text-gray-600 text-sm mb-2">
                Don't see the email? Check your spam folder
              </p>
            </div>

            {/* Resend Email - Future Feature */}
            <button 
              className="w-full flex items-center justify-center gap-2 px-4 py-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled
            >
              <RefreshCw className="w-4 h-4" />
              <span className="text-sm">Resend Email (Coming Soon)</span>
            </button>

            {/* Support Contact */}
            <div className="pt-2 border-t border-gray-100">
              <p className="text-gray-600 text-sm text-center mb-3">
                Still having trouble?
              </p>
              <a 
                href={`mailto:support@appealyourhome.com?subject=Account Setup Help&body=Session ID: ${sessionId}`}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                <span className="text-sm">Contact Support</span>
              </a>
            </div>

            {/* Already Have Account */}
            <div className="pt-2 border-t border-gray-100">
              <p className="text-gray-600 text-sm text-center mb-3">
                Already have an account?
              </p>
              <Link 
                to="/auth"
                className="w-full flex items-center justify-center gap-2 px-4 py-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">Sign In Instead</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-gray-500 text-xs">
            This email may take a few minutes to arrive. Please be patient.
          </p>
        </div>
      </div>
    </div>
  );
}